import React, { useState } from "react";
import ReactDatePicker from "react-datepicker";
import Calendar from "@bolteu/kalep-react-icons/dist/Calendar";
import ChevronLeft from "@bolteu/kalep-react-icons/dist/ChevronLeft";
import ChevronRight from "@bolteu/kalep-react-icons/dist/ChevronRight";
import Time from "@bolteu/kalep-react-icons/dist/Time";
import { IconButton } from "../IconButton";
import { TextField } from "../TextField";
import { useControlledValueWarning } from "../../helpers";
import { cx } from "../../helpers/utils/cx";
import { PredefinedRanges } from "./PredefinedRanges";
const DEFAULT_LOCALE = "en-US";
const DEFAULT_FORMAT = "dd/MM/yyyy";
const DEFAULT_TIME_FORMAT = "HH:mm";
const DEFAULT_START_DAY = 1;
const DEFAULT_ARIA_LABELS = {
  previousMonth: "Previous month",
  nextMonth: "Next month"
};
function DatePickerHeader(_ref) {
  let {
    date,
    locale,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    previousMonthAriaLabel,
    nextMonthAriaLabel,
    predefinedRanges,
    onRangeSelected
  } = _ref;
  const showPredefinedRangePicker = predefinedRanges && onRangeSelected;
  return /*#__PURE__*/React.createElement("div", null, showPredefinedRangePicker ? /*#__PURE__*/React.createElement(PredefinedRanges, {
    onRangeSelected: onRangeSelected,
    ranges: predefinedRanges
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: cx("flex items-center justify-between px-2 py-2")
  }, /*#__PURE__*/React.createElement("span", {
    className: cx("bolt-font-body-l-accent text-primary capitalize")
  }, `${date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short"
  })}`), /*#__PURE__*/React.createElement("div", {
    className: cx("flex gap-2")
  }, /*#__PURE__*/React.createElement(IconButton, {
    onClick: decreaseMonth,
    disabled: prevMonthButtonDisabled,
    size: "sm",
    variant: "secondary",
    icon: /*#__PURE__*/React.createElement(ChevronLeft, {
      className: cx("rtl:rotate-180")
    }),
    "aria-label": previousMonthAriaLabel ?? DEFAULT_ARIA_LABELS.previousMonth
  }), /*#__PURE__*/React.createElement(IconButton, {
    onClick: increaseMonth,
    disabled: nextMonthButtonDisabled,
    size: "sm",
    variant: "secondary",
    icon: /*#__PURE__*/React.createElement(ChevronRight, {
      className: cx("rtl:rotate-180")
    }),
    "aria-label": nextMonthAriaLabel ?? DEFAULT_ARIA_LABELS.nextMonth
  }))));
}
const renderDay = day => {
  return /*#__PURE__*/React.createElement("div", {
    className: cx("react-datepicker__day--internal")
  }, day);
};
const renderCalendarInputEndSlot = props => {
  return /*#__PURE__*/React.createElement(Calendar, {
    className: cx(props.disabled ? "text-tertiary" : "text-primary cursor-pointer"),
    size: props.size === "sm" ? "sm" : "lg"
  });
};
const renderTimeInputEndSlot = props => {
  return /*#__PURE__*/React.createElement(Time, {
    className: cx(props.disabled ? "text-tertiary" : "text-primary cursor-pointer"),
    size: props.size === "sm" ? "sm" : "lg"
  });
};
const DatePickerInput = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    value,
    label,
    placeholder,
    required,
    helperText,
    disabled,
    error,
    onClick,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    tabIndex,
    fullWidth,
    size,
    renderEndSlot,
    renderStartSlot
  } = _ref2;
  // We're manually passing through all the necessary props, because react-datepicker manages to pass on some kind of garbage
  // if we just spread all the props
  return /*#__PURE__*/React.createElement(TextField, {
    ref: ref,
    tabIndex: tabIndex,
    value: value,
    label: label,
    disabled: disabled,
    error: error,
    onClick: onClick,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    onKeyDown: onKeyDown,
    placeholder: placeholder,
    required: required,
    helperText: helperText,
    renderStartSlot: renderStartSlot,
    renderEndSlot: renderEndSlot ?? renderCalendarInputEndSlot,
    fullWidth: fullWidth,
    size: size
  });
});
export function DatePicker(props) {
  const {
    onChange,
    value,
    name,
    startValue,
    endValue,
    label,
    helperText,
    placeholder,
    required,
    locale,
    format,
    timeFormat,
    startDay,
    minDate,
    maxDate,
    minTime,
    maxTime,
    isDisabled,
    isError,
    isRange,
    previousMonthAriaLabel,
    nextMonthAriaLabel,
    fullWidth,
    size,
    hasTimeSelect,
    hasTimeSelectOnly,
    injectTimes,
    predefinedRanges,
    popperPlacement,
    popperModifiers,
    renderStartSlot,
    renderEndSlot
  } = props;
  let hasTimeSelectInternal = hasTimeSelect;
  if (hasTimeSelect && isRange) {
    hasTimeSelectInternal = false;
    // eslint-disable-next-line no-console -- expected to show warning
    console.warn("Kalep warning: Datepicker does not support time selection in a ranged Datepicker. 'hasTimeSelect' is ignored.");
  }
  const inputRef = React.useRef(null);
  const [internalDate, setInternalDate] = useState(null);
  const [internalStartDate, setInternalStartDate] = useState(null);
  const [internalEndDate, setInternalEndDate] = useState(null);
  const isControlled = React.useMemo(() => {
    return "value" in props || "startValue" in props || "endValue" in props;
  }, [props]);
  useControlledValueWarning(isControlled);
  const dateToUse = isControlled ? value : internalDate;
  const startDateToUse = isControlled ? startValue : internalStartDate;
  const endDateToUse = isControlled ? endValue : internalEndDate;
  const localeToUse = locale ?? DEFAULT_LOCALE;
  const formatToUse = format ?? DEFAULT_FORMAT;
  const timeFormatToUse = timeFormat ?? DEFAULT_TIME_FORMAT;
  const startDayToUse = startDay ?? DEFAULT_START_DAY;
  const placeholderToUse = placeholder ?? (isRange ? `${formatToUse} - ${formatToUse}` : formatToUse);
  const popperPlacementToUse = popperPlacement ?? "bottom-start";
  const handleChange = React.useCallback(newValue => {
    if (isControlled) {
      onChange?.(newValue);
    } else if (!isRange) {
      setInternalDate(newValue);
    } else {
      const [start, end] = newValue;
      setInternalStartDate(start);
      setInternalEndDate(end);
    }
  }, [isControlled, isRange, onChange]);

  /** Consider: perhaps we only need handleChange? */
  const handlePredefinedRangeSelected = React.useCallback(range => {
    if (!isRange) {
      return;
    }
    if (isControlled) {
      onChange?.(range);
      return;
    }
    setInternalStartDate(range[0]);
    setInternalEndDate(range[1]);
  }, [isControlled, isRange, onChange]);
  const endSlot = renderEndSlot ?? (hasTimeSelectOnly ? renderTimeInputEndSlot : undefined);
  return /*#__PURE__*/React.createElement("div", {
    className: cx(fullWidth ? "w-full" : "w-96")
  }, /*#__PURE__*/React.createElement(ReactDatePicker, {
    name: name,
    customInputRef: inputRef,
    onChange: handleChange,
    selected: isRange ? undefined : dateToUse,
    startDate: isRange ? startDateToUse : undefined,
    endDate: isRange ? endDateToUse : undefined,
    selectsRange: isRange,
    disabled: isDisabled,
    minDate: minDate,
    maxDate: maxDate,
    minTime: minTime,
    maxTime: maxTime,
    fixedHeight: true,
    showPopperArrow: false,
    showTimeSelect: hasTimeSelectInternal,
    showTimeSelectOnly: hasTimeSelectOnly,
    injectTimes: injectTimes,
    calendarStartDay: startDayToUse,
    dateFormat: formatToUse,
    timeFormat: timeFormatToUse,
    placeholderText: placeholderToUse,
    wrapperClassName: "kalep-datepicker",
    popperClassName: "kalep-datepicker",
    popperPlacement: popperPlacementToUse,
    popperModifiers: popperModifiers,
    customInput: /*#__PURE__*/React.createElement(DatePickerInput, {
      ref: inputRef,
      label: label,
      helperText: helperText,
      error: isError,
      disabled: isDisabled,
      required: required,
      fullWidth: fullWidth,
      size: size,
      renderStartSlot: renderStartSlot,
      renderEndSlot: endSlot
    }),
    locale: localeToUse,
    required: required,
    renderDayContents: renderDay
    // eslint-disable-next-line react/jsx-no-bind -- We need to pass locale as well
    ,
    renderCustomHeader: _ref3 => {
      let {
        date,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled
      } = _ref3;
      return /*#__PURE__*/React.createElement(DatePickerHeader, {
        date: date,
        locale: localeToUse,
        increaseMonth: increaseMonth,
        decreaseMonth: decreaseMonth,
        prevMonthButtonDisabled: prevMonthButtonDisabled,
        nextMonthButtonDisabled: nextMonthButtonDisabled,
        previousMonthAriaLabel: previousMonthAriaLabel,
        nextMonthAriaLabel: nextMonthAriaLabel,
        predefinedRanges: isRange ? predefinedRanges : undefined,
        onRangeSelected: handlePredefinedRangeSelected
      });
    }
  }));
}